import React from "react";
import s from "./SectionEndModal.module.scss";
import {AccuracyScore} from "./accuracyScore/AccuracyScore";
import {PointsAwarded} from "./pointsAwarded/PointsAwarded";
import {PointsUntilNextLevel} from "./pointsUntilNextLevel/PointsUntilNextLevel";
import {SectionEndAward} from "./sectionEndAward/SectionEndAward";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import {SectionAwardIcon} from "./sectionEndAward/sectionAwardIcon/SectionAwardIcon";
import {useAppSelector} from "../../../redux/hooks";
import {appRoutePaths, lessonPageUrlFragment} from "../../../config/config";
import LessonSection from "../../../models/view/lessonPage/LessonSection";
import {NavigateFunction, useNavigate} from "react-router-dom";
import {lessonPlayerStore} from "../../../redux/lesson/LessonPlayerStore";
import {deviceStore} from "../../../redux/device/deviceStore";

export interface ISectionEndModalProps {
  isLastSectionOfCourse?: boolean;
  section?: LessonSection;
  nextSection?: LessonSection;
  onReplayButtonClick: () => void;
}

export default function SectionEndModal({
  isLastSectionOfCourse,
  section,
  nextSection,
  onReplayButtonClick,
}: ISectionEndModalProps) {
  const shouldDisplaySectionEndModal = useAppSelector(lessonPlayerStore.getDisplaySectionEndModal);
  const scoreModalViewModel = useAppSelector(lessonPlayerStore.getScoreModalViewModel);
  const isMobile = useAppSelector(deviceStore.getDeviceIsMobile);
  const navigate = useNavigate();

  if(!shouldDisplaySectionEndModal){
    return null;
  }

  const icon = <div className={s.nextSectionIconWrapper}><SectionAwardIcon sectionNumber={nextSection?.displayNumber ?? 0} /></div>;
  const nextButtonLabel = isMobile ? 'Next' : 'Next Section';

  const hideSectionEndModal = () => lessonPlayerStore.setDisplaySectionEndModal(false);

  return (
    <Modal onClose={hideSectionEndModal} midiCloseShortcutNote={'B'}>
      <div className={s.sectionEndModal}>
        <SectionEndAward
          sectionTitle={section?.title ?? ""}
          sectionNumber={section?.displayNumber ?? 0}
          nextSectionTitle={nextSection?.title ?? ""}
          sectionId={section?.sectionId ?? 0}
          hasUnlockedSongs={scoreModalViewModel?.hasUnlockedSongs ?? false}
          closeTheModal={hideSectionEndModal}
        />
        <dl className={s.scoreAndPoints}>
          <AccuracyScore score={scoreModalViewModel?.correctNotesPercent ?? 0} />
          <PointsAwarded points={scoreModalViewModel?.pointsAwarded ?? 0} />
          <PointsUntilNextLevel pointsToLevel={scoreModalViewModel?.pointsUntilNextLevel ?? 0} />
        </dl>
        <div className={s.actionButtons}>
          <Button
            text={'Replay'}
            ariaLabel={'Replay this segment'}
            navigationNote={'C'}
            buttonType={"secondary"}
            onClick={() => { hideSectionEndModal(); onReplayButtonClick(); }}
          />
          {getPrimaryButton(
            isLastSectionOfCourse ?? false,
            nextButtonLabel,
            icon,
            (showModal: boolean) => { lessonPlayerStore.setDisplaySectionEndModal(showModal)},
            navigate,
            (lessonId: number) => { lessonPlayerStore.setDisplayNewSectionModalForLesson(lessonId)},
            nextSection,
          )}
        </div>
      </div>
    </Modal>
  );
}

function getPrimaryButton(
  isLastSectionOfCourse: boolean,
  nextButtonLabel: string,
  icon: JSX.Element,
  onClick: (isDisplayed: boolean) => void,
  navigate: NavigateFunction,
  showNewSectionModal: (lessonId: number) => void,
  nextSection?: LessonSection,
) {
  if (isLastSectionOfCourse) {
    return <Button
      text={'Done'}
      navigationNote={'D'}
      buttonType={'primary'}
      onClick={() => {
        onClick(false);
        navigate(appRoutePaths.dashboardPage);
      }}
    />
  }

  if (nextSection && nextSection.idOfFirstLesson) {
    return <Button
      icon={icon}
      text={nextButtonLabel}
      ariaLabel={'Go to the next section'}
      navigationNote={'D'}
      buttonType={"primary"}
      onClick={() => {
        onClick(false);

        showNewSectionModal(nextSection.idOfFirstLesson as number);
        navigate(`${lessonPageUrlFragment}${nextSection.idOfFirstLesson}`, {replace: true});
      }}
    />
  }

  return null;
}


